import { cloneDeep } from 'lodash-es';
import { useMemo } from 'react';
import styled from 'styled-components';
import { IMarketPriceFilter } from '../../../../../../../constants/filter/marketPriceFilter.constant';
import { IProductFilter } from '../../../../../../../constants/filter/productFilter.constant';
import { useGetMachinePriceStatisticsMeta } from '../../../../../../../hooks/api/marketPrice/queries/useGetMachinePriceStatisticsMeta.query';
import { useGetProductsQuery } from '../../../../../../../hooks/api/product/queries/useGetProducts.query';
import { FilterOptionType, FilterType, IFilter } from '../../../../../../../interfaces/Filter.interfaces';
import { createMarketPriceFilterSearchParams } from '../../../../../../../utils/filter/createMarketPriceFilterSearchParams.util';
import { createProductFilterSearchParams } from '../../../../../../../utils/filter/createProductFilterSearchParams.util';
import { Icon } from '../../../../../icon/Icon';
import { CheckboxCircle } from '../../../../../inputs/CheckboxCircle';
import { RadioCircle } from '../../../../../inputs/RadioCircle';

interface FilterInputProps {
  filter: IFilter;
  filterType: FilterType;
  option: FilterOptionType;
  handleClick: () => void;
  localFilters: IFilter[];
  isDisabled?: boolean;
}

export const FilterInput = ({
  filter,
  filterType,
  option,
  handleClick,
  localFilters,
  isDisabled,
}: FilterInputProps) => {
  const convertLocalProductFilterForPreFetch = useMemo<IFilter[]>(() => {
    if (filter.key !== 'filter_brand') {
      return [];
    }

    const cloneLocalProductFilters = cloneDeep(localFilters);

    return cloneLocalProductFilters.map((filter) => {
      if (filter.key === 'filter_brand') {
        filter.options = filter.options.map((filterOption) => {
          if (filterOption.optionName === option.optionName) {
            filterOption.isSelected = true;
          } else {
            filterOption.isSelected = false;
          }

          return filterOption;
        });
      }
      return filter;
    });
  }, [localFilters]);

  const {
    data: preProductsByBrand,
    isLoading: isPreProductsByBrandLoading,
    isSuccess: isPreProductsByBrandSuccess,
  } = useGetProductsQuery({
    searchParam: createProductFilterSearchParams({ filters: convertLocalProductFilterForPreFetch as IProductFilter[] }),
    options: {
      enabled: filterType === 'productFilter' && filter.key === 'filter_brand',
      staleTime: 1000 * 60 * 60 * 5,
    },
  });

  const {
    data: preMarketPriceProducts,
    isLoading: isPreMarketPriceProductsLoading,
    isSuccess: isPreMarketPriceProductsSuccess,
  } = useGetMachinePriceStatisticsMeta({
    searchParam: createMarketPriceFilterSearchParams({ filters: localFilters as IMarketPriceFilter[] }),
    options: {
      enabled: filterType === 'marketPriceFilter' && filter.key === 'filter_brand',
      staleTime: 1000 * 60 * 60 * 5,
    },
  });

  const preProductsByBrandCount = useMemo<string>(() => {
    if (isPreProductsByBrandLoading) {
      return '...';
    }

    if (isPreProductsByBrandSuccess && preProductsByBrand) {
      return `(${preProductsByBrand.length})`;
    }

    return '';
  }, [preProductsByBrand, isPreProductsByBrandLoading]);

  const marketPriceProductsByBrandCount = useMemo<string>(() => {
    if (isPreMarketPriceProductsLoading) {
      return '...';
    }

    if (isPreMarketPriceProductsSuccess && preMarketPriceProducts) {
      return `(${preMarketPriceProducts.brands.find((brand) => brand.id === option.value)?.tractorCount || 0})`;
    }

    return '';
  }, [preMarketPriceProducts, isPreMarketPriceProductsLoading, isPreMarketPriceProductsSuccess, option.value]);

  return (
    <Container onClick={handleClick} isDisabled={isDisabled}>
      <LeftSection>
        <LeftTopSection gap={filter.key === 'filter_brand' ? 2 : 10}>
          <Title>{option.optionName}</Title>
          {filterType === 'productFilter' && filter.key === 'filter_brand' && <Count>{preProductsByBrandCount}</Count>}
          {filterType === 'marketPriceFilter' && filter.key === 'filter_brand' && (
            <Count>{marketPriceProductsByBrandCount}</Count>
          )}
          {'chip' in option && option.chip === '할부 가능' && (
            <RedChip>
              <RedWonIconWrap>
                <Icon name={'RedWon'} width={11.2} height={11.2} />
              </RedWonIconWrap>
              <RedChipText>{option.chip}</RedChipText>
            </RedChip>
          )}
        </LeftTopSection>
        <LeftBottomSection>
          {'description' in option && option.description && <Description>{option.description}</Description>}
        </LeftBottomSection>
      </LeftSection>

      <RightSection>
        {filter.optionType === 'checkbox' && <CheckboxCircle isSelected={option.isSelected} />}
        {filter.optionType === 'radio' && <RadioCircle isSelected={option.isSelected} />}
      </RightSection>
    </Container>
  );
};

const Container = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  align-self: stretch;

  padding: var(--radius-large, 16px) 24px var(--radius-large, 16px) 40px;

  border-radius: var(--radius-medium, 8px);

  cursor: pointer;
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'auto')};
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  flex: 1 0 0;
`;

const LeftTopSection = styled.div<{ gap: number }>`
  display: flex;
  align-items: center;
  gap: ${(props) => `${props.gap}px`};
`;

const LeftBottomSection = styled.div``;

const Description = styled.span`
  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-overflow: ellipsis;

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%; /* 11.9px */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 13.6px */
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Circle = styled.div<{ hasBlueBackground: boolean }>`
  width: 16px;
  height: 16px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 2px;
  gap: 8px;

  border-radius: 50px;
  border: ${(props) => !props.hasBlueBackground && '1px solid var(--Light-Scheme-Monochrome-grey30, #d5d5e3)'};
  background: ${(props) => (props.hasBlueBackground ? '#445eff' : '#fff')};
`;

const RedChip = styled.div`
  display: flex;
  align-items: center;

  padding: var(--radius-small, 4px);
  gap: 2px;

  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Color-secondary-red, #ffdbe0);
`;

const RedChipText = styled.span`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const RedWonIconWrap = styled.div`
  width: 14px;
  height: 14px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 1.401px;
`;

const Count = styled.span`
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);

  /* System/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 13.6px */
`;
